import { useState } from 'react';
import { useNavigate, Form } from 'react-router';
import { 
  ArrowLeft, Bot, Search, Calendar, Settings, Building2, 
  MapPin, DollarSign, Clock, Plus, AlertTriangle
} from 'lucide-react';
import { Card, Button, Badge } from '../../../components/ui';

export function CreateAutoApplyRulePage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [ruleName, setRuleName] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');

  const steps = [
    { id: 1, title: 'Basic Info', icon: Settings },
    { id: 2, title: 'Search Criteria', icon: Search },
    { id: 3, title: 'Schedule', icon: Calendar },
    { id: 4, title: 'Job Boards', icon: Building2 },
    { id: 5, title: 'Review', icon: Bot },
  ];

  const addKeyword = () => {
    if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
      setKeywords([...keywords, newKeyword.trim()]);
      setNewKeyword('');
    }
  };

  const removeKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/auto-apply')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Bot className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Create Auto-Apply Rule</h1>
        </div>
        <p className="text-muted-foreground ml-11">
          Set up automated job applications with smart targeting and scheduling.
        </p>
      </div>

      {/* Progress Steps */}
      <Card>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                  isActive ? 'bg-primary/10 text-primary' :
                  isCompleted ? 'bg-green-500/10 text-green-500' :
                  'text-muted-foreground'
                }`}>
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium hidden sm:block">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-px mx-2 ${
                    step.id < currentStep ? 'bg-green-500' : 'bg-border'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Step Content */}
      {currentStep === 1 && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground">Basic Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="e.g., Frontend Developer - Remote"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (optional)
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe this rule and its purpose..."
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {currentStep === 2 && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground">Search Criteria</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Keywords *
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="e.g., React, TypeScript, Frontend"
                      className="flex-1 px-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <Button onClick={addKeyword} size="sm" className="gap-1">
                      <Plus className="h-4 w-4" />
                      Add
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((keyword) => (
                      <Badge 
                        key={keyword} 
                        variant="secondary" 
                        className="gap-1 cursor-pointer hover:bg-destructive/10"
                        onClick={() => removeKeyword(keyword)}
                      >
                        {keyword}
                        <span className="text-xs ml-1">×</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., San Francisco, CA or Remote"
                    className="w-full px-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Experience Level
                  </label>
                  <select className="w-full px-4 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40">
                    <option value="">Any Level</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="lead">Lead/Manager</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Min Salary ($)
                  </label>
                  <input
                    type="number"
                    placeholder="50000"
                    className="w-full px-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Max Salary ($)
                  </label>
                  <input
                    type="number"
                    placeholder="150000"
                    className="w-full px-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remote-only"
                  className="rounded border-border text-primary focus:ring-primary/50"
                />
                <label htmlFor="remote-only" className="text-sm text-foreground">
                  Remote positions only
                </label>
              </div>
            </div>
          </div>
        </Card>
      )}

      {currentStep === 3 && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Schedule
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Execution Frequency
                </label>
                <div className="space-y-2">
                  {[
                    { value: 'immediate', label: 'Immediate', desc: 'Apply as soon as jobs are found' },
                    { value: 'daily', label: 'Daily', desc: 'Run once per day at a specified time' },
                    { value: 'weekly', label: 'Weekly', desc: 'Run once per week on specified days' },
                  ].map((option) => (
                    <label key={option.value} className="flex items-start gap-3 p-3 border border-border rounded-xl cursor-pointer hover:bg-muted/30">
                      <input
                        type="radio"
                        name="schedule"
                        value={option.value}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-foreground">{option.label}</div>
                        <div className="text-sm text-muted-foreground">{option.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Application Limits
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      Max applications per day
                    </label>
                    <input
                      type="number"
                      defaultValue={5}
                      min={1}
                      max={50}
                      className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      Max applications per week
                    </label>
                    <input
                      type="number"
                      defaultValue={25}
                      min={1}
                      max={200}
                      className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {currentStep === 4 && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground">Job Boards</h3>
            
            <div className="space-y-4">
              {[
                { name: 'LinkedIn', desc: 'Professional networking platform', supported: true },
                { name: 'Indeed', desc: 'Global job search engine', supported: true },
                { name: 'Glassdoor', desc: 'Jobs and company reviews', supported: false },
                { name: 'ZipRecruiter', desc: 'Job matching platform', supported: false },
              ].map((board) => (
                <div key={board.name} className="flex items-center justify-between p-4 border border-border rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-xl">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {board.name}
                        {!board.supported && (
                          <Badge variant="secondary" className="text-xs">
                            Coming Soon
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{board.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {board.supported ? (
                      <>
                        <input
                          type="checkbox"
                          defaultChecked={board.name === 'LinkedIn'}
                          className="rounded border-border text-primary focus:ring-primary/50"
                        />
                        <Button variant="secondary" size="sm" disabled={!board.supported}>
                          Configure
                        </Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" disabled>
                        Configure
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {currentStep === 5 && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-foreground">Review & Create</h3>
            
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Beta Feature Notice</h4>
                    <p className="text-sm text-muted-foreground">
                      Auto-apply is currently in beta. We recommend starting with a test run to verify 
                      the rule works as expected before activating it for real applications.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-foreground">Rule Summary</h4>
                <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name:</span>
                    <span className="text-foreground font-medium">{ruleName || 'Untitled Rule'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Keywords:</span>
                    <span className="text-foreground">{keywords.join(', ') || 'None specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Schedule:</span>
                    <span className="text-foreground">Daily execution</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Job Boards:</span>
                    <span className="text-foreground">LinkedIn</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Navigation */}
      <Card>
        <div className="flex justify-between">
          <Button 
            variant="secondary" 
            onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/auto-apply')}
          >
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </Button>
          
          <div className="flex gap-2">
            {currentStep === 5 ? (
              <>
                <Form method="post">
                  <input type="hidden" name="intent" value="save_draft" />
                  <input type="hidden" name="ruleName" value={ruleName} />
                  <input type="hidden" name="keywords" value={JSON.stringify(keywords)} />
                  <Button type="submit" variant="secondary">
                    Save as Draft
                  </Button>
                </Form>
                <Form method="post">
                  <input type="hidden" name="intent" value="create_and_test" />
                  <input type="hidden" name="ruleName" value={ruleName} />
                  <input type="hidden" name="keywords" value={JSON.stringify(keywords)} />
                  <Button type="submit">
                    Create & Test Run
                  </Button>
                </Form>
              </>
            ) : (
              <Button onClick={() => setCurrentStep(currentStep + 1)}>
                Next
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}